import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from './ReadingDayFrom.module.scss';
import CustomDatePicker from "../../../../components/customDatePicker/CustomDatePicker";
import PropTypes from "prop-types";
import { format } from "date-fns";
import Dropdown from "../../../../components/dropdown/Dropdown";
import TimePicker from "../../../../components/timePicker/TimePicker";
import {ReactComponent as AlertCircleIcon} from './AlertCircle.svg';
import { compareTimes } from "../../../../utils/timeUtils";

const ReadingDayFrom = forwardRef(({
  formData,
  studentYearList = [],
  onSave,
  disabled = false
}, ref) => {
  const [dateOfCase, setDateOfCase] = useState(null);
  const [dateOfCaseError, setDateOfCaseError] = useState('');
  const [yearId, setYearId] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [endTime, setEndTime] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  useEffect(() => {
    setDateOfCase(formData?.dateOfCase ? new Date(formData.dateOfCase) : null);
    setYearId(formData?.yearId || null);
    setStartTime(formData?.startTime || '');
    setEndTime(formData?.endTime || '');
  }, [formData]);

  const validateDateOfCase = () => {
    if(!dateOfCase) {
      return 'Date of Case is required';
    }
    return '';
  }

  const validateStartTime = () => {
    if(!startTime) {
      return 'Start Time is required';
    }
    if(endTime && !compareTimes(startTime, endTime)) {
      return 'Start time cannot be later than or equal to end time';
    }
    return '';
  }

  const validateEndTime = () => {
    if(!endTime) {
      return 'End Time is required';
    }
    if(startTime && !compareTimes(startTime, endTime)) {
      return 'End time cannot be earlier than or equal to start time';
    }
    return '';
  }

  const validateForm = () => {
    const dateOfCaseValidationResult = validateDateOfCase();
    const startTimeValidationResult = validateStartTime();
    const endTimeValidationResult = validateEndTime();
    
    setDateOfCaseError(dateOfCaseValidationResult);
    setStartTimeError(startTimeValidationResult);
    setEndTimeError(endTimeValidationResult);

    const errors = [
      dateOfCaseValidationResult,
      startTimeValidationResult,
      endTimeValidationResult,
    ]
  
    const errorCount = errors.filter(error => error !== '').length;
  
    return errorCount;
  };

  // Expose the function to the parent
  useImperativeHandle(ref, () => ({
    validateForm
  }));

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={styles.row}>
        <div className={styles.column20}>
          <CustomDatePicker
            label="Date of Case"
            date={dateOfCase}
            onChange={(date) => {
              console.log(date);
              setDateOfCase(date);
              onSave({
                dateOfCase: date ? format(date, "yyyy-MM-dd") : null
              })
            }}
            showIcon={true}
            maxDate={new Date()}
            error={dateOfCaseError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <Dropdown
            label="Year"
            options={studentYearList}
            value={yearId}
            onChange={(value) => {
              setYearId(value);
            }}
            disabled={true}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={(value) => {
              setStartTime(value);
              onSave({
                startTime: value
              })
            }}
            error={startTimeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <TimePicker
            label="End Time"
            value={endTime}
            onChange={(value) => {
              setEndTime(value);
              onSave({
                endTime: value
              })
            }}
            error={endTimeError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.info}>
        <AlertCircleIcon />
        <span>
          You have selected a Non-Clinical Day - Reading Day without a Clinical Instructor or Case.
          If you need to provide additional information, please contact your Program Director directly
        </span>
      </div>
    </form>
  );
});

ReadingDayFrom.displayName = 'ReadingDayFrom';

ReadingDayFrom.propTypes = {
  formData: PropTypes.object,
  studentYearList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default ReadingDayFrom;